import React, { useState, useEffect, useCallback } from 'react';
import Pagination from '../Pagination';
import axios from '../../api/api';
import { MdOutlineBlock } from 'react-icons/md';
import { FaSearch } from "react-icons/fa";
import Loader from '../Loader';

const StkPushArchieve = () => {
  const [stkpusharchieve, setStkpusharchieve] = useState([]);
  const [searchStkpusharchieve, setSearchStkpusharchieve] = useState('');
  const [searchTransactionId, setSearchTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(15);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searched, setSearched] = useState(false); // Tracks if search has been performed

  // Convert a date to partition format pYYYYMMDD
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch data only when the "Search" button is clicked
  const getStkpusharchieve = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let query = `?offset=${(currentPage - 1) * recordsPerPage}&limit=${recordsPerPage}`;

      if (!searchStkpusharchieve && !searchTransactionId) {
        setLoading(false);
        return;
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 5) {
          alert('Date range cannot exceed 5 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);
        while (current <= end) {
          partitions.push(formatDateToPartition(current));
          current.setDate(current.getDate() + 1);
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      if (searchStkpusharchieve) query += `&msisdn=${searchStkpusharchieve}`;
      if (searchTransactionId) query += `&tran_id=${searchTransactionId}`;

      const response = await axios.get(`/requests/stk_push_archive${query}`);
      setStkpusharchieve(response.data.items);
      setCount(response.data.count);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      setError('Error fetching data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchStkpusharchieve, searchTransactionId, startDate, endDate]);

  // Handle search button click
  const handleSearch = () => {
    setSearched(true);
    setCurrentPage(1);
    getStkpusharchieve();
  };

  // Handle clearing search inputs
  const handleClearSearch = () => {
    setSearchStkpusharchieve('');
    setSearchTransactionId('');
    setStartDate('');
    setEndDate('');
    setStkpusharchieve([]); 
    setSearched(false); 
  };

  // Fetch data only if a search has been performed
  useEffect(() => {
    if (searched) getStkpusharchieve();
  }, [currentPage, recordsPerPage]);

  return (
    <div className='mx-auto p-4'>
      <h3 className="my-2"><b>Payment Logs</b></h3>
      <div className='bg-white rounded-lg p-4 xl:w-[78vw] 2xl:w-full'>
        <div className="flex flex-wrap items-center justify-between py-2">
          <div className='flex flex-wrap gap-2 items-center'>
            <div className='py-2 flex flex-wrap gap-2'>
              <input
                type='search'
                className='px-3 py-1 border bg-[#eef1ff] border-slate-300 rounded-md'
                placeholder='Search msisdn'
                value={searchStkpusharchieve}
                onChange={(e) => setSearchStkpusharchieve(e.target.value)}
              />
              <input
                type='search'
                className='px-3 py-1 border bg-[#eef1ff] border-slate-300 rounded-md'
                placeholder='Search Transaction Id'
                value={searchTransactionId}
                onChange={(e) => setSearchTransactionId(e.target.value)}
              />
              <input
                type="date"
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 rounded-md'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <input
                type="date"
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 rounded-md'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <button
                onClick={handleSearch}
                className='px-4 py-1 bg-[#2b1e3e] text-white rounded-md'
              >
                Search
              </button>
              <button
                onClick={handleClearSearch}
                className='px-4 py-1 bg-red-500 text-white rounded-md'
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className='flex items-center justify-center bg-[#eef1ff] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='grid place-items-center h-[70vh]'>
            <div className='text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : searched && stkpusharchieve.length === 0 ? (
          <div className='grid place-items-center h-[70vh]'>
            <div className='text-[#134463] p-4'>
              <h3><MdOutlineBlock /></h3>
              <h4>No Data Found</h4>
            </div>
          </div>
        ) : (
          searched ? (
            <div className='overflow-x-auto rounded-lg'>
              <table className='w-full text-left table-auto'>
                <thead>
                  <tr className='border-b border-slate-500'>
                    <th className='p-2'>Transaction Id</th>
                    <th className='p-2'>Msisdn</th>
                    <th className='p-2'>Amount</th>
                    <th className='p-2'>Account Number</th>
                    <th className='p-2'>Provider</th>
                    <th className='p-2'>Ussd</th>
                    <th className='p-2'>Narration</th>
                    <th className='p-2'>Short Code</th>
                    <th className='p-2'>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {stkpusharchieve.map((stkpush) => (
                    <tr key={stkpush.id}>
                      <td className='p-2'>{stkpush.tran_id}</td>
                      <td className='p-2'>{stkpush.msisdn}</td>
                      <td className='p-2'>{stkpush.amount}</td>
                      <td className='p-2'>{stkpush.account_no}</td>
                      <td className='p-2'>{stkpush.provider}</td>
                      <td className='p-2'>{stkpush.ussd}</td>
                      <td className='p-2'>{stkpush.customer_message}</td>
                      <td className='p-2'>{stkpush.shortcode}</td>
                      <td className='p-2'>{stkpush.date_created ? new Date(stkpush.date_created)?.toLocaleString("en-CA", { hour12: false }) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='grid place-items-center h-[70vh]'>
              <div className='text-[#134463] p-4'>
                <h3><FaSearch /></h3>
                <h4>Search Data</h4>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default StkPushArchieve;
